import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Papa from 'papaparse'
import { api } from '../utils/api'
import { X, Plus, Copy, Trash2, Download, Upload, RotateCcw, Columns3 } from 'lucide-react'

type Option = { label: string; value: string }
type ColumnKind = 'text' | 'number' | 'date' | 'type' | 'status' | 'category' | 'brand' | 'department'

interface TemplateColumn {
  key: string
  // CSV header — must be a key recognised by FIELD_MAP in ImportPage so the file imports as-is
  header: string
  label: string
  kind: ColumnKind
  group: string
  required?: boolean
  placeholder?: string
  width?: number
}

export const TEMPLATE_COLUMNS: TemplateColumn[] = [
  { key: 'assetCode', header: 'ASSET_CODE', label: 'Asset Code', kind: 'text', group: 'ข้อมูลพื้นฐาน', required: true, placeholder: 'N0286', width: 130 },
  { key: 'assetTag', header: 'รหัสทรัพย์สิน', label: 'Asset Tag', kind: 'text', group: 'ข้อมูลพื้นฐาน', placeholder: 'CZ03-21000016', width: 140 },
  { key: 'name', header: 'ชื่อ', label: 'ชื่อ', kind: 'text', group: 'ข้อมูลพื้นฐาน', placeholder: 'ว่าง = ใช้ Asset Code', width: 150 },
  { key: 'type', header: 'ประเภท', label: 'ประเภท', kind: 'type', group: 'ข้อมูลพื้นฐาน', width: 130 },
  { key: 'category', header: 'หมวดหมู่', label: 'หมวดหมู่', kind: 'category', group: 'ข้อมูลพื้นฐาน', width: 140 },
  { key: 'serialNumber', header: 'serial number', label: 'Serial Number', kind: 'text', group: 'ข้อมูลพื้นฐาน', placeholder: 'S/N', width: 140 },
  { key: 'brand', header: 'ยี่ห้อ', label: 'ยี่ห้อ', kind: 'brand', group: 'ข้อมูลพื้นฐาน', width: 130 },
  { key: 'model', header: 'model', label: 'Model', kind: 'text', group: 'ข้อมูลพื้นฐาน', placeholder: 'ThinkPad L14', width: 150 },
  { key: 'spec', header: 'สเปค', label: 'สเปค', kind: 'text', group: 'ข้อมูลพื้นฐาน', placeholder: 'Ryzen 5 / 16GB', width: 170 },
  { key: 'status', header: 'สถานะ', label: 'สถานะ', kind: 'status', group: 'ข้อมูลพื้นฐาน', width: 130 },
  { key: 'building', header: 'อาคาร', label: 'อาคาร', kind: 'text', group: 'ตำแหน่ง & ผู้ใช้', width: 110 },
  { key: 'floor', header: 'ชั้น', label: 'ชั้น', kind: 'text', group: 'ตำแหน่ง & ผู้ใช้', width: 80 },
  { key: 'room', header: 'ห้อง', label: 'ห้อง', kind: 'text', group: 'ตำแหน่ง & ผู้ใช้', width: 110 },
  { key: 'department', header: 'หน่วยงาน', label: 'แผนก', kind: 'department', group: 'ตำแหน่ง & ผู้ใช้', width: 140 },
  { key: 'employeeId', header: 'รหัสพนักงาน', label: 'รหัสพนักงาน', kind: 'text', group: 'ตำแหน่ง & ผู้ใช้', placeholder: 'IL67001', width: 120 },
  { key: 'assignedTo', header: 'ผู้ใช้', label: 'ผู้ใช้', kind: 'text', group: 'ตำแหน่ง & ผู้ใช้', placeholder: 'username', width: 130 },
  { key: 'ipAddress', header: 'ip address', label: 'IP Address', kind: 'text', group: 'เครือข่าย', placeholder: '192.168.1.10', width: 130 },
  { key: 'macEthernet', header: 'ethernet mac', label: 'Ethernet MAC', kind: 'text', group: 'เครือข่าย', width: 150 },
  { key: 'macWifi', header: 'wifi mac', label: 'WiFi MAC', kind: 'text', group: 'เครือข่าย', width: 150 },
  { key: 'purchasePrice', header: 'ราคา', label: 'ราคา', kind: 'number', group: 'การเงิน', placeholder: '23500', width: 110 },
  { key: 'purchaseDate', header: 'วันที่ซื้อ', label: 'วันที่ซื้อ', kind: 'date', group: 'การเงิน', width: 150 },
  { key: 'warrantyExpiry', header: 'วันหมดประกัน', label: 'วันหมดประกัน', kind: 'date', group: 'การเงิน', width: 150 },
  { key: 'costCenter', header: 'cost center', label: 'Cost Center', kind: 'text', group: 'การเงิน', width: 120 },
  { key: 'notes', header: 'หมายเหตุ', label: 'หมายเหตุ', kind: 'text', group: 'อื่นๆ', width: 180 },
]

const DEFAULT_COLUMNS = ['assetCode', 'type', 'category', 'serialNumber', 'brand', 'model', 'spec', 'status', 'employeeId', 'assignedTo', 'purchasePrice', 'purchaseDate']
const DRAFT_KEY = 'assetTemplateBuilder.v1'

type BuilderRow = Record<string, string>

function emptyRow(): BuilderRow {
  return { type: 'LAPTOP', status: 'AVAILABLE' }
}

function loadDraft(): { columns: string[]; rows: BuilderRow[] } | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (!raw) return null
    const d = JSON.parse(raw)
    if (Array.isArray(d.columns) && Array.isArray(d.rows)) return d
  } catch { /* ignore unavailable storage */ }
  return null
}

interface Props {
  onClose: () => void
  // Rows keyed by CSV header — fed straight into the normal CSV preview step
  onUseRows: (rows: Record<string, string>[]) => void
}

export default function AssetTemplateBuilder({ onClose, onUseRows }: Props) {
  const draft = useMemo(loadDraft, [])
  const [columns, setColumns] = useState<string[]>(draft?.columns ?? DEFAULT_COLUMNS)
  const [rows, setRows] = useState<BuilderRow[]>(draft?.rows?.length ? draft.rows : [emptyRow()])
  const [showColumns, setShowColumns] = useState(false)

  const { data: assetTypes = [] } = useQuery<Option[]>({ queryKey: ['masterdata', 'asset-types'], queryFn: () => api.get('/masterdata/asset-types').then(r => r.data) })
  const { data: assetStatuses = [] } = useQuery<Option[]>({ queryKey: ['masterdata', 'asset-statuses'], queryFn: () => api.get('/masterdata/asset-statuses').then(r => r.data) })
  const { data: categories = [] } = useQuery<{ id: string; name: string }[]>({ queryKey: ['masterdata', 'asset-categories'], queryFn: () => api.get('/masterdata/asset-categories').then(r => r.data) })
  const { data: brands = [] } = useQuery<{ id: string; name: string }[]>({ queryKey: ['masterdata', 'brands'], queryFn: () => api.get('/masterdata/brands').then(r => r.data) })
  const { data: departments = [] } = useQuery<{ id: string; name: string }[]>({ queryKey: ['masterdata', 'departments'], queryFn: () => api.get('/masterdata/departments').then(r => r.data).catch(() => []) })

  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ columns, rows })) } catch { /* ignore unavailable storage */ }
  }, [columns, rows])

  const activeCols = TEMPLATE_COLUMNS.filter(c => c.required || columns.includes(c.key))
  const groups = Array.from(new Set(TEMPLATE_COLUMNS.map(c => c.group)))

  const toggleColumn = (key: string) =>
    setColumns(cs => cs.includes(key) ? cs.filter(k => k !== key) : [...cs, key])

  const setCell = (i: number, key: string, value: string) =>
    setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [key]: value } : r))

  const addRow = () => setRows(rs => [...rs, emptyRow()])
  const duplicateRow = (i: number) =>
    setRows(rs => [...rs.slice(0, i + 1), { ...rs[i], assetCode: '', serialNumber: '' }, ...rs.slice(i + 1)])
  const deleteRow = (i: number) => setRows(rs => rs.length > 1 ? rs.filter((_, idx) => idx !== i) : [emptyRow()])
  const reset = () => {
    if (!confirm('ล้างข้อมูลที่กรอกทั้งหมด?')) return
    setColumns(DEFAULT_COLUMNS); setRows([emptyRow()])
  }

  const filledRows = rows.filter(r => activeCols.some(c => c.key !== 'type' && c.key !== 'status' && r[c.key]?.trim()))
  const missingCode = filledRows.filter(r => !r.assetCode?.trim()).length

  const toCsvRows = () => filledRows.map(r => Object.fromEntries(activeCols.map(c => [c.header, (r[c.key] ?? '').trim()])))

  const download = () => {
    const csv = Papa.unparse({ fields: activeCols.map(c => c.header), data: toCsvRows().map(r => activeCols.map(c => r[c.header])) })
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `asset-import-${new Date().toISOString().slice(0, 10)}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  const inputCls = 'w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  const renderCell = (col: TemplateColumn, row: BuilderRow, i: number) => {
    const value = row[col.key] ?? ''
    const onChange = (e: { target: { value: string } }) => setCell(i, col.key, e.target.value)
    switch (col.kind) {
      case 'type':
      case 'status': {
        const opts = col.kind === 'type' ? assetTypes : assetStatuses
        const known = !value || opts.some(o => o.value === value)
        return (
          <select value={value} onChange={onChange} className={inputCls}>
            <option value="">—</option>
            {!known && <option value={value}>{value}</option>}
            {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )
      }
      case 'category':
      case 'brand':
      case 'department':
        return <input list={`tb-${col.kind}`} value={value} onChange={onChange} className={inputCls} placeholder="เลือกหรือพิมพ์" />
      case 'date':
        return <input type="date" value={value} onChange={onChange} className={inputCls} />
      case 'number':
        return <input type="number" inputMode="decimal" value={value} onChange={onChange} className={inputCls} placeholder={col.placeholder} />
      default:
        return <input value={value} onChange={onChange} className={`${inputCls} ${col.required && !value.trim() && filledRows.includes(row) ? 'border-red-300 bg-red-50' : ''}`} placeholder={col.placeholder} />
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-7xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <datalist id="tb-category">{categories.map(c => <option key={c.id} value={c.name} />)}</datalist>
        <datalist id="tb-brand">{brands.map(b => <option key={b.id} value={b.name} />)}</datalist>
        <datalist id="tb-department">{departments.map(d => <option key={d.id} value={d.name} />)}</datalist>

        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">สร้าง Template นำเข้า Assets</h2>
            <p className="text-sm text-gray-500 mt-0.5">เลือกคอลัมน์ กรอกข้อมูล แล้วดาวน์โหลดเป็นไฟล์ CSV หรือนำเข้าได้ทันที (ข้อมูลที่กรอกจะถูกเก็บไว้ในเบราว์เซอร์นี้)</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <button onClick={() => setShowColumns(s => !s)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${showColumns ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
            <Columns3 size={14} /> เลือกคอลัมน์ ({activeCols.length})
          </button>
          <button onClick={addRow} className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
            <Plus size={14} /> เพิ่มแถว
          </button>
          <button onClick={reset} className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50">
            <RotateCcw size={14} /> ล้างข้อมูล
          </button>
          <span className="ml-auto text-xs text-gray-500">
            {filledRows.length} แถวที่มีข้อมูล
            {missingCode > 0 && <span className="text-red-600"> · {missingCode} แถวยังไม่มี Asset Code</span>}
          </span>
        </div>

        {showColumns && (
          <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {groups.map(g => (
              <div key={g}>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1.5">{g}</p>
                <div className="space-y-1">
                  {TEMPLATE_COLUMNS.filter(c => c.group === g).map(c => (
                    <label key={c.key} className={`flex items-center gap-2 text-sm ${c.required ? 'text-gray-400' : 'text-gray-700 cursor-pointer'}`}>
                      <input type="checkbox" checked={c.required || columns.includes(c.key)} disabled={c.required} onChange={() => toggleColumn(c.key)} className="rounded border-gray-300" />
                      {c.label}{c.required && <span className="text-red-500">*</span>}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          <table className="text-xs border-separate border-spacing-0">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left text-gray-500 font-semibold border-b border-gray-200 w-10">#</th>
                {activeCols.map(c => (
                  <th key={c.key} style={{ minWidth: c.width }} className="px-2 py-2 text-left text-gray-600 font-semibold border-b border-gray-200 whitespace-nowrap">
                    {c.label}{c.required && <span className="text-red-500">*</span>}
                    <span className="block font-normal text-[10px] text-gray-400 font-mono">{c.header}</span>
                  </th>
                ))}
                <th className="px-2 py-2 border-b border-gray-200 w-16" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="hover:bg-blue-50/30">
                  <td className="px-2 py-1 text-gray-400 border-b border-gray-50">{i + 1}</td>
                  {activeCols.map(c => <td key={c.key} className="px-1 py-1 border-b border-gray-50">{renderCell(c, row, i)}</td>)}
                  <td className="px-1 py-1 border-b border-gray-50 whitespace-nowrap">
                    <button onClick={() => duplicateRow(i)} title="ทำซ้ำแถวนี้" className="p-1.5 text-gray-400 hover:text-blue-600 rounded"><Copy size={13} /></button>
                    <button onClick={() => deleteRow(i)} title="ลบแถว" className="p-1.5 text-gray-400 hover:text-red-600 rounded"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addRow} className="m-3 flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg">
            <Plus size={13} /> เพิ่มแถว
          </button>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-xl text-sm hover:bg-gray-50">ปิด</button>
          <button onClick={download} className="flex items-center gap-2 px-4 py-2 border border-blue-300 text-blue-700 rounded-xl text-sm hover:bg-blue-50">
            <Download size={15} /> ดาวน์โหลด CSV{filledRows.length ? ` (${filledRows.length} แถว)` : ' (หัวคอลัมน์)'}
          </button>
          <button onClick={() => onUseRows(toCsvRows())} disabled={filledRows.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            <Upload size={15} /> ตรวจสอบและนำเข้า
          </button>
        </div>
      </div>
    </div>
  )
}
